// src/components/visitors/VisitorsList.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Plane, Fuel, Plus, RefreshCw } from 'lucide-react';
import TransactionModal from '../fuel/TransactionModal';
import RegisterAircraftModal from './RegisterAircraftModal';
import ConvertVisitorModal from './ConvertVisitorModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const VisitorsList = ({ onConvert, onRefresh }) => {
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [owners, setOwners] = useState([]);

  // Modal states
  const [showFuelModal, setShowFuelModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);

  // Fuel data
  const [inventory, setInventory] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [savingTx, setSavingTx] = useState(false);
  const [txForm, setTxForm] = useState({
    aircraft_id: '', owner_id: '', fuel_type: 'JetA',
    gallons: '', price_per_gallon: '', staff_id: '', truck_id: '',
  });

  // Billing info for visitors
  const [visitorBilling, setVisitorBilling] = useState({
    contact_name: '',
    company_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'USA'
  });

  // Fetch data
  const fetchVisitors = async () => {
    try {
      const res = await axios.get(`${API_BASE}/tracking/visits?limit=50`);
      setVisitors(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching visitors:', err);
      setVisitors([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchOwners = async () => {
    try {
      const res = await axios.get(`${API_BASE}/owners`);
      setOwners(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching owners:', err);
    }
  };

  const fetchFuelData = async () => {
    try {
      const [invRes, truckRes, staffRes] = await Promise.all([
        axios.get(`${API_BASE}/fuel/inventory`),
        axios.get(`${API_BASE}/fuel/trucks`),
        axios.get(`${API_BASE}/staff/`),
      ]);
      const inv = Array.isArray(invRes.data) ? invRes.data : [];
      setInventory(inv);
      setTrucks(Array.isArray(truckRes.data) ? truckRes.data : []);
      setStaff(Array.isArray(staffRes.data) ? staffRes.data : []);
      if (inv.length > 0) {
        setTxForm(prev => ({
          ...prev,
          fuel_type: inv[0].fuel_type,
          price_per_gallon: inv[0].price_per_gallon ? String(inv[0].price_per_gallon) : '',
        }));
      }
    } catch (err) {
      console.error('Error fetching fuel data:', err);
    }
  };

  useEffect(() => {
    fetchVisitors();
    fetchOwners();
    fetchFuelData();
  }, []);
  
  useEffect(() => {
    if (onRefresh) fetchVisitors();
  }, [onRefresh]);

  // Handlers
  const openFuelModal = (visitor) => {
    setSelectedVisitor(visitor);
    setVisitorBilling({
      contact_name: '',
      company_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      country: 'USA'
    });
    setTxForm(prev => ({
      ...prev,
      aircraft_id: '',
      owner_id: '',
      staff_id: '',
      truck_id: '',
      gallons: '',
    }));
    setShowFuelModal(true);
  };

  const handleSaveFuelTx = async (billingInfo = null) => {
    if (!txForm.gallons || !txForm.price_per_gallon) {
      alert('Please fill in gallons and price.');
      return;
    }
    
    // En mode visiteur, le nom du contact est requis
    if (billingInfo && !billingInfo.contact_name) {
      alert('Contact name is required for billing/invoice.');
      return;
    }
    
    setSavingTx(true);
    try {
      const payload = {
        gallons: parseFloat(txForm.gallons),
        fuel_type: txForm.fuel_type,
        price_per_gallon: parseFloat(txForm.price_per_gallon),
        visit_id: selectedVisitor.id,
        truck_id: txForm.truck_id || undefined,
        staff_id: txForm.staff_id || undefined,
      };
      
      // Ajouter les infos de facturation si fournies (mode visiteur)
      if (billingInfo) {
        payload.billing_info = billingInfo;
      }
      
      await axios.post(`${API_BASE}/fuel/visitor-transaction`, payload);
      setShowFuelModal(false);
      fetchVisitors();
      alert(`Fuel added successfully for ${selectedVisitor.tail_number}`);
    } catch (err) {
      console.error('Error saving fuel:', err);
      alert('Error saving fuel transaction: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingTx(false);
    }
  };

  const handleRegisterSuccess = () => {
    fetchVisitors();
    if (onConvert) onConvert();
  };

  const handleConvertSuccess = () => {
    fetchVisitors();
    if (onConvert) onConvert();
  };

  // Helpers
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const countMovements = (movements, type) => {
    if (!movements) return 0;
    return movements.filter(m => m.type === type).length;
  };

  if (loading) {
    return (
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          <span className="ml-2 text-gray-400">Loading visitors...</span>
        </div>
      </div>
    );
  }

  if (visitors.length === 0) {
    return (
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
        <div className="flex items-center gap-3 text-gray-400">
          <Users size={20} />
          <span>No unregistered aircraft visits yet</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-700 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-yellow-400" />
          <h3 className="text-white font-semibold">Aircraft Visitors (Unregistered)</h3>
          <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full">
            {visitors.length}
          </span>
        </div>
        <button 
          onClick={() => { fetchVisitors(); fetchFuelData(); }} 
          className="text-gray-400 hover:text-white text-xs transition flex items-center gap-1"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* List */}
      <div className="divide-y divide-gray-700 max-h-[400px] overflow-y-auto">
        {visitors.map(visitor => (
          <div key={visitor.id} className="p-4 hover:bg-gray-700/30 transition">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Plane size={16} className="text-gray-400" />
                  <span className="font-mono font-bold text-white text-base">{visitor.tail_number}</span>
                  {visitor.callsign && visitor.callsign !== visitor.tail_number && (
                    <span className="text-xs text-gray-500">({visitor.callsign})</span>
                  )}
                  {visitor.fuel_added > 0 && (
                    <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">
                      ⛽ {visitor.fuel_added} gal
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 xl:grid-cols-4 gap-2 text-xs">
                  <div className="min-w-0">
                    <span className="text-gray-500">First seen</span>
                    <p className="text-gray-300 text-sm truncate">{formatDate(visitor.first_seen)}</p>
                  </div>
                  <div className="min-w-0">
                    <span className="text-gray-500">Last seen</span>
                    <p className="text-gray-300 text-sm truncate">{formatDate(visitor.last_seen)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Takeoffs</span>
                    <p className="text-green-400 font-semibold">{countMovements(visitor.movements, 'TAKEOFF')}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Landings</span>
                    <p className="text-orange-400 font-semibold">{countMovements(visitor.movements, 'LANDING')}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 ml-4">
                {/* ⛽ Fuel button */}
                <button
                  onClick={() => openFuelModal(visitor)}
                  className="p-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-400 transition"
                  title="Add fuel"
                >
                  <Fuel size={16} />
                </button>
                {/* ➕ Register button */}
                <button
                  onClick={() => {
                    setSelectedVisitor(visitor);
                    setShowRegisterModal(true);
                  }}
                  className="p-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-400 transition"
                  title="Register aircraft"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Fuel Modal - Visitor Mode */}
      <TransactionModal
        isOpen={showFuelModal}
        onClose={() => {
          setShowFuelModal(false);
          setSelectedVisitor(null);
        }}
        editingTx={null}
        txForm={txForm}
        setTxForm={setTxForm}
        inventory={inventory}
        aircraft={[]}
        owners={owners}
        staff={staff}
        trucks={trucks}
        onSave={handleSaveFuelTx}
        saving={savingTx}
        isVisitor={true}
        visitorTailNumber={selectedVisitor?.tail_number}
        visitorBilling={visitorBilling}
        setVisitorBilling={setVisitorBilling}
      />

      {/* Register Aircraft Modal */}
      <RegisterAircraftModal
        isOpen={showRegisterModal}
        selectedVisitor={selectedVisitor}
        clients={owners}
        onClose={() => {
          setShowRegisterModal(false);
          setSelectedVisitor(null);
        }}
        onSuccess={handleRegisterSuccess}
        fetchOwners={fetchOwners}
      />

      {/* Convert Modal */}
      <ConvertVisitorModal
        isOpen={showConvertModal}
        selectedVisitor={selectedVisitor}
        owners={owners}
        onClose={() => {
          setShowConvertModal(false);
          setSelectedVisitor(null);
        }}
        onSuccess={handleConvertSuccess}
      />
    </div>
  );
};

export default VisitorsList;